import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { fetchMenuByCategory } from '../../app/menubycategory/menubycategorySlice';
import { MENU_IMAGE_PATH, image_url } from '../../Utils/baseUrl';
import commonFunction from '../../Utils/commonFunction';
import { stripHtml, truncate } from '../../Utils/html';
import Loader from '../Common/Loader';
import { useContentLoader } from '../../Utils/useContentLoader';
import Seo from '../../Seo/Seo';
import { webPageSchema, itemListSchema } from '../../Seo/jsonld';

function MenuByCategory() {

    const { idCategory } = useParams();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(fetchMenuByCategory(idCategory));
        window.scrollTo(0, 0)
    }, [idCategory, dispatch]);

    const menus = useSelector((state) => state.menuByCategory);
    const menuList = menus.getMenubyCategory?.data?.menus || [];

    // The category is fetched directly rather than being read off the first
    // menu in the list. Deriving it from `menuList[0].Category` leaves an empty
    // category with no name at all, which showed up as five category pages
    // sharing one generic fallback title.
    const [category, setCategory] = useState(null);

    useEffect(() => {
        let cancelled = false;
        commonFunction
            .fetchDataById("categories", idCategory)
            .then((res) => {
                if (!cancelled) setCategory(res?.data || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [idCategory]);

    const categoryName =
        stripHtml(category?.title) || stripHtml(menuList[0]?.Category?.title) || "Skolemad";
    const categoryDescription =
        stripHtml(category?.description) || stripHtml(menuList[0]?.Category?.description);

    // Not prerendered either: wait for the category before drawing the page,
    // otherwise the heading briefly reads the generic fallback.
    const showLoader = useContentLoader(!category);

    const path = `/menubyCategory/${idCategory}`;

    const title = `${categoryName} | Skolemad til skoler | Onlineskolemad`;
    const description = categoryDescription
        ? truncate(`${categoryName} – ${categoryDescription}`)
        : truncate(
            `Se vores udvalg af ${categoryName.toLowerCase()} til skolefrokost. Bestil nemt online og få maden leveret direkte til dit barns skole.`
        );

    const bannerImage = menuList[0]?.MenuImages?.[0]?.image
        ? `${image_url}${MENU_IMAGE_PATH}${menuList[0].MenuImages[0].image}`
        : undefined;

    if (showLoader) return <Loader />;

    return (
        <React.Fragment>
            <main>
                <Seo
                    title={title}
                    description={description}
                    path={path}
                    image={bannerImage}
                    // Same readiness contract as the dish page: `categoryName`
                    // falls back to a generic label before the category loads,
                    // so stay noindex until the real name is in hand and let the
                    // prerenderer wait for it.
                    index={Boolean(stripHtml(category?.title))}
                    jsonLd={[
                        webPageSchema({ title, description, url: path, image: bannerImage }),
                        itemListSchema({
                            name: categoryName,
                            url: path,
                            items: menuList.map((item) => ({
                                name: stripHtml(item.title),
                                path: `/menuDetails/${item.idMenu}`,
                            })),
                        }),
                    ]}
                />
                <section className="page-info-sec">
                    <div
                        className="bg-fixed wow fadeIn"
                        data-wow-delay="0.1s"
                        style={{ background: 'url("/assets/img/page-info1.png")' }}
                    />
                    <div className="bg-fixed img-overlay wow fadeIn" data-wow-delay="0.2s" />
                    <div className="container">
                        <div className="row align-items-center justify-content-center">
                            <div className="col-md-12">
                                <div className="page-info-block wow fadeInUp" data-wow-delay="0.3s">
                                    {/* Was a hard-coded English "Category Details" heading. */}
                                    <h1>{categoryName}</h1>
                                    <div className="btm-line-2 text-center justify-content-center">
                                        <span />
                                        <span />
                                    </div>
                                    <p>
                                        {categoryDescription ||
                                            `Se vores udvalg af ${categoryName.toLowerCase()} til skolefrokost – nemt at bestille online og leveret direkte til skolen.`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="sec-pd  bg-gray">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div
                                    className="title-1 title-center wow fadeInUp"
                                    data-wow-delay="0.2s"
                                >
                                    {/* Was "Menus in Category" + Lorem ipsum. */}
                                    <h2 className="title-1-h">Retter i {categoryName}</h2>
                                    <div className="btm-line-1 text-center justify-content-center">
                                        <span />
                                        <span />
                                    </div>
                                    <p>
                                        {menuList.length > 0
                                            ? `${menuList.length} retter i kategorien ${categoryName}. Klik på en ret for at se ingredienser og næringsindhold.`
                                            : `Der er i øjeblikket ingen retter i kategorien ${categoryName}.`}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="row wow fadeInUp">
                            {menuList.map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-6 d-flx" key={item.idMenu ?? index}>
                                    <div className="menu-card">
                                        <div className="menu-card-img">
                                            <img
                                                src={item?.MenuImages?.[0]?.image ? `${image_url}${MENU_IMAGE_PATH}` + item.MenuImages[0].image : ''}
                                                alt={`${stripHtml(item.title)} – ${categoryName}`}
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="menu-desc">
                                            <div className="menu-desc-head">
                                                <h5>{stripHtml(item.title)}</h5>
                                            </div>
                                            <p>{stripHtml(item.description)}</p>
                                            <NavLink className="btn btn-1" to={`/menuDetails/${item.idMenu}`}>
                                                View more
                                            </NavLink>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

        </React.Fragment>
    )
}

export default MenuByCategory
